import { HookOutOfContextError } from "@jugl-web/utils";
import isEqual from "lodash/isEqual";
import {
  Context,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

interface UseGenericFiltersStateOptions<TState extends object> {
  initialState: TState;
  emptyState: TState;
  getActiveFiltersCount: (state: TState) => number;
}

type ValueCallback<TValue> = (previousValue: TValue) => TValue;

export type UpdateFiltersFn<TState> = <
  TField extends keyof TState,
  TValue extends TState[TField]
>(
  field: TField,
  value: TValue | ValueCallback<TValue>
) => void;

const isValueCallback = <TValue,>(
  value: TValue | ValueCallback<TValue>
): value is ValueCallback<TValue> => typeof value === "function";

/**
 * A hook to manage the state of generic filters.
 * @template TState - The type of the filters state object.
 * @param options - Configuration options for the hook.
 * @returns An object containing the filters state and utility functions.
 */
export const useGenericFiltersState = <TState extends object>({
  initialState,
  emptyState,
  getActiveFiltersCount,
}: UseGenericFiltersStateOptions<TState>) => {
  const [internalInitialState, setInternalInitialState] =
    useState(initialState);

  const [filtersState, setFiltersState] = useState(internalInitialState);

  const activeFiltersCount = useMemo(
    () => getActiveFiltersCount(filtersState),
    [filtersState, getActiveFiltersCount]
  );

  const hasActiveFilter = activeFiltersCount > 0;

  /**
   * Checks if the current filters state is different from the initial state.
   * @returns True if the filters state is dirty, false otherwise.
   */
  const isDirty = useCallback(
    () => !isEqual(filtersState, internalInitialState),
    [filtersState, internalInitialState]
  );

  /**
   * Updates a specific field in the filters state.
   * @param field - The field to update.
   * @param value - The new value or a callback to calculate the new value.
   */
  const updateFiltersState = useCallback<UpdateFiltersFn<TState>>(
    (field, value) => {
      setFiltersState((previousFilters) => ({
        ...previousFilters,
        [field]: isValueCallback(value)
          ? value(previousFilters[field] as Parameters<typeof value>[0])
          : value,
      }));
    },
    []
  );

  /**
   * Clears the filters state, resetting it to the empty state.
   */
  const clearFiltersState = useCallback(() => {
    setFiltersState(emptyState);
  }, [emptyState]);

  /**
   * Resets the filters state to a new state or the initial state.
   * @param newState - The new state to reset to. Defaults to the initial state.
   */
  const resetFiltersState = useCallback(
    (newState = initialState) => {
      setInternalInitialState(newState);
      setFiltersState(newState);
    },
    [initialState]
  );

  const filtersStateContext = {
    filtersState,
    activeFiltersCount,
    hasActiveFilter,
    isDirty,
    updateFiltersState,
    clearFiltersState,
    resetFiltersState,
  };

  return {
    ...filtersStateContext,
    filtersStateContext,
  };
};

// #region Context-related stuff
type GenericFiltersStateContextValue<TState extends object> = ReturnType<
  typeof useGenericFiltersState<TState>
>["filtersStateContext"];

const GenericFiltersStateContext =
  createContext<GenericFiltersStateContextValue<object> | null>(null);

export interface GenericFiltersStateProviderProps<TState extends object> {
  children: ReactNode;
  context: GenericFiltersStateContextValue<TState>;
}

export const GenericFiltersStateProvider = <TState extends object>({
  children,
  context,
}: GenericFiltersStateProviderProps<TState>) => {
  const TypedContext =
    GenericFiltersStateContext as Context<GenericFiltersStateContextValue<TState> | null>;

  return (
    <TypedContext.Provider value={context}>{children}</TypedContext.Provider>
  );
};

export const useGenericFiltersStateContext = <TState extends object>() => {
  const context = useContext(
    GenericFiltersStateContext
  ) as GenericFiltersStateContextValue<TState> | null;

  if (!context) {
    throw new HookOutOfContextError(
      "useGenericFiltersStateContext",
      "GenericFiltersStateProvider"
    );
  }

  return context;
};
// #endregion
